import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaCheckCircle, FaTimesCircle, FaMapMarkerAlt, FaPhone, FaEnvelope, FaTimes, FaSearch, FaFilter, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import axios from 'axios';

const ItemSelector = () => {
  const [players, setPlayers] = useState([]);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  // Fetch players from API
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        setLoading(true);
        const response = await axios.get(' https://ksa-manager-back.onrender.com/fetch_registered_players');
        
        if (response.data.success) {
          const playersWithStatus = response.data.players.map(player => ({
            ...player,
            passed_requirement: player.status === 'approved',
            location: `${player.town}, ${player.district}, ${player.region}`
          }));
          
          setPlayers(playersWithStatus);
        } else {
          setError('Failed to fetch players data');
        }
      } catch (err) {
        console.error('Error fetching players:', err);
        setError('Error connecting to the server');
      } finally {
        setLoading(false);
      }
    };

    fetchPlayers();
  }, []);

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return 'N/A';
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Sort players
  const sortPlayers = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort players
  const filteredPlayers = players
    .filter(player => {
      const fullName = `${player.first_name} ${player.last_name}`.toLowerCase();
      const matchesSearch = fullName.includes(searchTerm.toLowerCase()) || 
                           player.address.toLowerCase().includes(searchTerm.toLowerCase());
      
      if (filterStatus === 'passed') {
        return matchesSearch && player.passed_requirement;
      } else if (filterStatus === 'failed') {
        return matchesSearch && !player.passed_requirement;
      } else {
        return matchesSearch;
      }
    })
    .sort((a, b) => {
      if (!sortConfig.key) return 0;
      
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPlayers.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredPlayers.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Open modal with player details
  const openPlayerModal = (player) => {
    setSelectedPlayer(player);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedPlayer(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6 md:p-12">
      {/* Header Section */}
      <div className="max-w-7xl mx-auto mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          Registered Players
        </h1>
        <p className="text-gray-600">
          View and manage all registered academy players
        </p>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto mb-8 flex flex-col md:flex-row gap-4">
        <div className="relative flex-grow">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FaSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search by name or location..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
            onClick={() => setFilterStatus('all')}
          >
            All
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'passed'
                ? 'bg-green-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
            onClick={() => setFilterStatus('passed')}
          >
            Approved
          </button>
          <button
            className={`px-4 py-2 rounded-lg ${
              filterStatus === 'failed'
                ? 'bg-red-600 text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
            onClick={() => setFilterStatus('failed')}
          >
            Pending
          </button>
        </div>
      </div>

      {/* Loading and Error States */}
      {loading && (
        <div className="max-w-7xl mx-auto text-center py-12">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <p className="mt-4 text-gray-600">Loading players data...</p>
        </div>
      )}

      {error && (
        <div className="max-w-7xl mx-auto bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-8">
          <strong className="font-bold">Error: </strong>
          <span className="block sm:inline">{error}</span>
        </div>
      )}

      {/* Players Table */}
      {!loading && !error && (
        <div className="max-w-7xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortPlayers('first_name')}
                  >
                    Name {sortConfig.key === 'first_name' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortPlayers('age')}
                  >
                    Age {sortConfig.key === 'age' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortPlayers('position')}
                  >
                    Position {sortConfig.key === 'position' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => sortPlayers('passed_requirement')}
                  >
                    Status {sortConfig.key === 'passed_requirement' && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.map((player) => (
                  <tr 
                    key={player.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => openPlayerModal(player)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <FaUser className="text-blue-500" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {player.first_name} {player.last_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{calculateAge(player.date_of_birth)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{player.position}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{player.phone}</div>
                      <div className="text-sm text-gray-500">{player.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        player.passed_requirement 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {player.passed_requirement ? 'Approved' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      View Details
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{indexOfFirstItem + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(indexOfLastItem, filteredPlayers.length)}
                  </span>{' '}
                  of <span className="font-medium">{filteredPlayers.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <FaChevronLeft className="h-5 w-5" />
                  </button>
                  {[...Array(totalPages)].map((_, index) => (
                    <button
                      key={index + 1}
                      onClick={() => paginate(index + 1)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === index + 1
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {index + 1}
                    </button>
                  ))}
                  <button
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50"
                  >
                    <FaChevronRight className="h-5 w-5" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Player Details Modal */}
      <AnimatePresence>
        {isModalOpen && selectedPlayer && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            onClick={closeModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    Player Details
                  </h2>
                  <button
                    onClick={closeModal}
                    className="text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Personal Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600">Full Name:</div>
                      <div className="font-medium">
                        {selectedPlayer.first_name} {selectedPlayer.last_name}
                      </div>
                      
                      <div className="text-gray-600">Date of Birth:</div>
                      <div className="font-medium">
                        {selectedPlayer.date_of_birth ? new Date(selectedPlayer.date_of_birth).toLocaleDateString() : 'N/A'} 
                        ({calculateAge(selectedPlayer.date_of_birth)} years)
                      </div>
                      
                      <div className="text-gray-600">Nationality:</div>
                      <div className="font-medium">{selectedPlayer.nationality}</div>
                      
                      <div className="text-gray-600">Gender:</div>
                      <div className="font-medium capitalize">{selectedPlayer.gender}</div>
                    </div>
                  </div>
                  
                  {/* Football Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Football Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600">Position:</div>
                      <div className="font-medium">{selectedPlayer.position}</div>
                      
                      <div className="text-gray-600">Preferred Foot:</div>
                      <div className="font-medium capitalize">{selectedPlayer.preferred_foot}</div>
                      
                      <div className="text-gray-600">Height:</div>
                      <div className="font-medium">{selectedPlayer.height} cm</div>
                      
                      <div className="text-gray-600">Weight:</div>
                      <div className="font-medium">{selectedPlayer.weight} kg</div>
                      
                      <div className="text-gray-600">Previous Club:</div>
                      <div className="font-medium">{selectedPlayer.previous_club}</div>
                      
                      <div className="text-gray-600">Status:</div>
                      <div className={`font-medium ${
                        selectedPlayer.passed_requirement 
                          ? 'text-green-600' 
                          : 'text-yellow-600'
                      }`}>
                        {selectedPlayer.passed_requirement ? 'Approved' : 'Pending'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Academic Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Academic Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600">Current School:</div>
                      <div className="font-medium">{selectedPlayer.current_school}</div>
                      
                      <div className="text-gray-600">Grade:</div>
                      <div className="font-medium">{selectedPlayer.grade}</div>
                      
                      <div className="text-gray-600">Achievements:</div>
                      <div className="font-medium">{selectedPlayer.academic_achievements}</div>
                    </div>
                  </div>
                  
                  {/* Guardian Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-700 border-b pb-2">
                      Guardian Information
                    </h3>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-gray-600">Name:</div>
                      <div className="font-medium">{selectedPlayer.guardian_name}</div>
                      
                      <div className="text-gray-600">Relationship:</div>
                      <div className="font-medium capitalize">{selectedPlayer.relationship}</div>
                      
                      <div className="text-gray-600">Phone:</div>
                      <div className="font-medium">{selectedPlayer.phone}</div>
                      
                      <div className="text-gray-600">Email:</div>
                      <div className="font-medium">{selectedPlayer.email}</div>
                      
                      <div className="text-gray-600">Address:</div>
                      <div className="font-medium">{selectedPlayer.address}</div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 flex justify-end">
                  <button
                    onClick={closeModal}
                    className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ItemSelector; 